import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { endpoints, type QuotationListResponse } from "@/lib/endpoints";
import { queryKeys } from "@/lib/query-keys";

/**
 * The quotation register (§7, §4).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * VOID QUOTATIONS ARE HIDDEN UNLESS ASKED FOR, and the SERVER does that — not
 * this file. `quotationListWhere` applies `status: { not: VOID }` whenever no
 * status filter is sent, per §4's rule that a cancelled quotation stays in the
 * book but off the everyday list.
 *
 * That is the OPPOSITE of the invoice register, where cancelled bills are shown
 * and marked (§17.4), and the difference is deliberate: a cancelled INVOICE
 * keeps a consumed number in a gap-free series and must remain visible; a voided
 * QUOTATION is an offer that was withdrawn, and the owner has no reason to see
 * it beside live ones.
 *
 * THE URL IS THE FILTER STATE, exactly as on the bill register. Not `useState`:
 * a filtered register has to survive a reload, come back with the back button,
 * and be sendable to the son over WhatsApp as a link that opens the same rows.
 * Component state gives none of those, and it fails silently — the screen looks
 * right until someone refreshes it and is quietly shown the whole book.
 *
 * `q` IS REAL ON THIS ENDPOINT since the scanner amendment (§9): it matches the
 * quotation's OWN reference numbers, `quote_number` and the customer's
 * `po_ref_no`. It does NOT match the customer name — the buyer lives in
 * `customer_snapshot`, a text-backed JSON column the server will not query
 * inside (CLAUDE.md). Finding a customer's quotations is `customerId`.
 *
 * THE AMOUNT RANGE BOUNDS THE FIGURE THE ROW PRINTS, NOT `grand_total_paise`
 * (§9.3), and the server is what makes that true — this file only carries the
 * two paise integers. A quotation row shows `display.printedGrandTotalPaise`:
 * the PRE-TAX subtotal when tax is extra, which is the default and the majority,
 * and the tax-inclusive grand total otherwise. So a row reading "₹6,500.00 + tax
 * extra" is matched at ₹6,500 even though it stores a grand total of ₹7,670 —
 * `quotationListWhere` ORs the two display modes to keep the filter and the
 * screen talking about the same number.
 *
 * THERE IS STILL NO `issuedManually` HERE, and there never will be (§6.1). The
 * flag records that a TAX INVOICE was hand-written in the owner's book and its
 * number back-filled into the gap-free series; a quotation has no such paper
 * series, and `issued_manually` is a column on `model Invoice` alone. A control
 * for it would be a 400 from a `.strict()` schema rather than an ignored
 * parameter.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export const QUOTATION_PAGE_SIZE = 50;

/** The columns the API will sort by. A closed set — see the route's schema. */
export type QuotationSortKey =
  | "number"
  | "date"
  | "customer"
  | "subject"
  | "status"
  | "total";

const QUOTATION_SORT_KEYS: readonly QuotationSortKey[] = [
  "number",
  "date",
  "customer",
  "subject",
  "status",
  "total",
];

export interface QuotationFilters {
  status?: string;
  customerId?: number;
  /**
   * Free text over the quotation's OWN reference numbers — `quote_number` and
   * the customer's `po_ref_no`. NOT the customer name; that is `customerId`.
   */
  q?: string;
  /** `YYYY-MM-DD`, INCLUSIVE, on `quote_date`. */
  from?: string;
  to?: string;
  /**
   * INTEGER PAISE, inclusive. `30000000` is ₹3,00,000.
   *
   * These bound the PRINTED total — `printedGrandTotalPaise`, the figure the row
   * shows — not `grand_total_paise`. See the header. The rupee→paise conversion
   * happens in the input control; everything from here to the wire is paise.
   */
  minTotalPaise?: number;
  maxTotalPaise?: number;
  /** Column sorting (§11.2 amendment) — in the URL, so a sorted view is linkable. */
  sort?: QuotationSortKey;
  dir?: "asc" | "desc";
  offset: number;
}

export function useQuotationFilters(): {
  filters: QuotationFilters;
  setFilters: (patch: Partial<QuotationFilters>) => void;
  clearFilters: () => void;
  activeCount: number;
} {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<QuotationFilters>(() => {
    const status = params.get("status") ?? undefined;
    const rawCustomer = params.get("customerId");
    const customerId = rawCustomer === null || rawCustomer === "" ? undefined : Number(rawCustomer);
    const rawOffset = params.get("offset");
    const q = params.get("q") ?? undefined;

    /** A bound that is not a whole number is no filter — never send `NaN`. */
    const paise = (raw: string | null): number | undefined => {
      if (raw === null || raw.trim() === "") return undefined;
      const value = Number(raw);
      return Number.isSafeInteger(value) && value >= 0 ? value : undefined;
    };

    return {
      status: status === "" ? undefined : status,
      q: q === "" ? undefined : q,
      // A junk `?customerId=abc` becomes "no filter" rather than `NaN`, which
      // would serialise into the request and earn a 400 the owner cannot read.
      customerId: customerId === undefined || !Number.isFinite(customerId) ? undefined : customerId,
      from: params.get("from") ?? undefined,
      to: params.get("to") ?? undefined,
      minTotalPaise: paise(params.get("minTotalPaise")),
      maxTotalPaise: paise(params.get("maxTotalPaise")),
      // A hand-edited `?sort=whatever` degrades to no sort rather than a 400.
      sort: QUOTATION_SORT_KEYS.find((key) => key === params.get("sort")),
      dir: params.get("dir") === "asc" ? "asc" : params.get("dir") === "desc" ? "desc" : undefined,
      offset: rawOffset === null ? 0 : Math.max(0, Number(rawOffset) || 0),
    };
  }, [params]);

  const setFilters = useCallback(
    (patch: Partial<QuotationFilters>) => {
      const next = new URLSearchParams(params);
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined || value === "") next.delete(key);
        else next.set(key, String(value));
      }
      /*
       * ANY FILTER CHANGE RETURNS TO PAGE ONE. Narrowing the register while
       * still sitting at offset 150 shows an empty page, which reads as
       * "nothing matches" when in fact three things do.
       */
      if (!("offset" in patch)) next.delete("offset");
      // `replace` so a filter fiddle does not stack twenty history entries
      // between the owner and the screen he came from.
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const clearFilters = useCallback(() => {
    setParams(new URLSearchParams(), { replace: true });
  }, [setParams]);

  const activeCount = [
    filters.status,
    filters.customerId,
    filters.q,
    // A range counts once, however many ends it has — "2 filters" for one date
    // range would make the badge read as more than is actually on.
    filters.from ?? filters.to,
    filters.minTotalPaise ?? filters.maxTotalPaise,
  ].filter((value) => value !== undefined).length;

  return { filters, setFilters, clearFilters, activeCount };
}

export function useQuotationList(
  filters: QuotationFilters,
): UseQueryResult<QuotationListResponse> {
  const query = {
    status: filters.status,
    customerId: filters.customerId,
    q: filters.q,
    from: filters.from,
    to: filters.to,
    // A key in `QuotationFilters` but not in THIS object is a filter that shows
    // in the URL and does nothing — the register would look narrowed and return
    // the whole book.
    minTotalPaise: filters.minTotalPaise,
    maxTotalPaise: filters.maxTotalPaise,
    limit: QUOTATION_PAGE_SIZE,
    sort: filters.sort,
    dir: filters.dir,
    offset: filters.offset === 0 ? undefined : filters.offset,
  };

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const qs = search.toString();

  return useQuery({
    queryKey: queryKeys.quotations.list(query),
    queryFn: () =>
      api.get<QuotationListResponse>(`${endpoints.quotations}${qs === "" ? "" : `?${qs}`}`),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}
